/**
 * Path / PM subject + program catalog.
 * Source of truth mirrored from dn-legacy admin/path/entry.php
 * ($response->values->subjects / subjectsOrder / programs / programsOrder).
 * Keep in sync when PM path options change.
 */

export type PmProgram = {id: number; name: string; subjectId: number};

/** Display order + labels (no subject ids shown in token key). */
export const PM_SUBJECTS_ORDER = [
    "Business",
    "Counseling and Psychology",
    "Criminal Justice",
    "Education",
    "Healthcare",
    "Information and Technology",
    "Art and Design",
    "Liberal Arts and Humanities",
    "Science and Engineering",
    "Other",
] as const;

const SUBJECT_ID_BY_NAME: Record<string, number> = {
    "Art and Design": 1,
    Business: 2,
    "Counseling and Psychology": 3,
    "Criminal Justice": 4,
    Education: 6,
    Healthcare: 8,
    "Information and Technology": 9,
    Other: 10,
    "Liberal Arts and Humanities": 11,
    "Science and Engineering": 12,
};

/** program_id => label, grouped by subject_id (from entry.php $programs). */
const PROGRAMS_BY_SUBJECT: Record<number, Record<number, string>> = {
    1: {
        3: "Art & Design",
        2: "Architectural Design & Drafting Technology",
        10: "Digital Photography",
        5: "Audio/Video Production",
        187: "Music",
        1: "Animation",
        13: "Graphic Design",
        8: "Digital Filmmaking & Video Production",
        6: "Design & Technical Graphics",
        11: "Game Art & Design",
        12: "Graphic and Web Design",
        20: "Web Design & Interactive Media",
        16: "Visual & Game Programming",
        220: "Acting / Theater",
    },
    2: {
        26: "Business Management",
        24: "Business",
        27: "Entrepreneurship",
        21: "Accounting",
        25: "Business Administration",
        179: "Marketing",
        32: "Management",
        29: "Finance",
        199: "Forensic Accounting",
        181: "Real Estate",
        36: "Project Management",
        34: "Office Management",
        180: "Operations Management",
        158: "Human Resource Management",
        156: "Hospitality Management",
        178: "e-Business",
        31: "Leadership",
        28: "Executive Leadership",
        30: "International Business",
        35: "Organizational Leadership",
        23: "Advertising",
    },
    3: {
        37: "Addictions",
        43: "Counseling",
        40: "Clinical Mental Health Counseling",
        42: "Community Counseling",
        44: "Counseling Psychology",
        54: "Psychology",
        55: "School Counseling",
        38: "Applied Behavior Analysis",
        49: "Marriage & Family Therapy",
        50: "Mental Health Counseling",
        51: "Pastoral Community Counseling",
        47: "Forensic Psychology",
        41: "Clinical Psychology",
        39: "Clinical Health Psychology",
        52: "Professional Counseling",
        45: "Counselor Education & Supervision",
        53: "Psychoanalytic Psychology",
        56: "School Psychology",
    },
    4: {
        59: "Criminal Justice",
        58: "Crime Scene Technician",
        63: "Law",
        57: "Corrections",
        221: "Forensic Science",
        60: "Criminal Justice Administration and Management",
        61: "Homeland Security and Emergency Management",
        66: "Paralegal",
        67: "Paralegal Assistant",
        65: "Legal Studies",
        64: "Legal Secretary",
    },
    6: {
        75: "Early Childhood Education",
        76: "Education",
        72: "Adult Education & Training",
        74: "Early Childhood Development",
        88: "Teaching & Learning",
        82: "Higher Education",
        78: "Educational Administration",
        80: "Educational Paraprofessional (Teacher's Aide)",
        85: "Literacy & Language",
        79: "Educational Leadership",
        87: "Mathematics Teaching",
        77: "Education in Instructional Technology",
    },
    8: {
        121: "Nursing",
        114: "Medical Coding and Billing",
        95: "RN to BSN",
        109: "Healthcare",
        110: "Healthcare Administration",
        111: "Healthcare Management",
        200: "Health and Wellness",
        132: "Radiologic Technology",
        113: "Medical Assisting",
        100: "Dental Assistant",
        127: "Physical Therapist Assistant",
        125: "Pharmacy",
        98: "Anesthesiologist Assistant",
        104: "Health and Fitness Training",
        129: "Practical Nursing",
        103: "Family Nurse Practitioner",
        102: "Diagnostic Medical Sonography",
        126: "Pharmacy Technology",
        101: "Dental Hygiene",
        133: "Surgical Technology",
        116: "Medical Office Management",
        115: "Medical Laboratory Technology",
        124: "Occupational Therapy Assistant",
        130: "Public Health",
        123: "Nutrition Science",
        107: "Health Science",
        108: "Health Services Management",
        122: "Nursing Completion",
        106: "Health Information Technology",
        131: "Radiation Therapy",
        118: "Medical Transcription",
        119: "Nurse Administrator",
        117: "Medical Technology",
        128: "Physician Assistant Studies",
        99: "Biomedical Equipment Technology",
    },
    9: {
        142: "Information Technology",
        140: "Cyber Security",
        204: "Computer Science",
        141: "Information Systems Management",
        201: "Information Technology Management",
        203: "Software Engineer",
        196: "Web Developer",
        202: "Database Administration",
        137: "Networking Administration",
    },
    11: {
        192: "Liberal Arts",
        197: "Social Work",
        194: "Religious Studies",
        191: "Communications",
        190: "Writing",
        193: "History",
    },
    12: {
        216: "Biological Science",
        188: "Math and Science",
        189: "Environmental Science",
        217: "Mortuary Science",
        218: "Animal Science",
        219: "Engineering",
    },
    10: {
        177: "Automotive",
        186: "Truck Driving",
        182: "Cosmetology",
        183: "Culinary",
        184: "Fashion",
        198: "Trade and Vocational",
        195: "Fire Science",
        205: "Agriculture/Horticulture",
        206: "Aviation",
        207: "Barbering",
        208: "Carpentry",
        209: "Childcare",
        210: "HVAC",
        211: "Electrician",
        212: "Interior Design",
        213: "Landscaping",
        214: "Massage Therapy",
        215: "Welding",
        176: "Veterinarian",
        185: "Not Listed",
    },
};

/** Display order from entry.php $programsOrder (what PM actually shows). */
const PROGRAMS_ORDER: Record<number, number[]> = {
    1: [3, 187, 10, 13, 2, 5, 1, 8, 6, 11, 12, 20, 16, 220],
    2: [26, 24, 27, 21, 25, 179, 32, 29, 199, 181, 36, 34, 180, 158, 156, 178, 31, 28, 30, 35, 23],
    3: [37, 43, 40, 42, 44, 54, 55, 38, 49, 50, 51, 47, 41, 39, 52, 45, 53, 56],
    4: [59, 58, 63, 57, 60, 61, 66, 67, 65, 64],
    6: [75, 76, 72, 74, 88, 82, 78, 80, 85, 79, 87, 77],
    8: [121, 114, 113, 95, 109, 200, 100, 110, 132, 127, 125, 98, 104, 129, 103, 102, 126, 101, 111, 133, 116, 115, 124, 130, 123, 107, 108, 122, 106, 131, 118, 119, 117, 128, 99],
    9: [142, 140, 204, 141, 201, 203, 196, 202, 137],
    11: [192, 197, 194, 191, 190, 193],
    12: [216, 217, 218, 219, 188, 189],
    10: [177, 186, 176, 198, 215, 208, 210, 182, 207, 214, 209, 211, 205, 183, 184, 195, 206, 212, 213, 185],
};

/** Flat ordered program list as shown on the path (PM). */
export function getPmPrograms(): PmProgram[] {
    const out: PmProgram[] = [];
    for (const subjectName of PM_SUBJECTS_ORDER) {
        const subjectId = SUBJECT_ID_BY_NAME[subjectName];
        const order = PROGRAMS_ORDER[subjectId] || [];
        const labels = PROGRAMS_BY_SUBJECT[subjectId] || {};
        for (const id of order) {
            const name = labels[id];
            if (name) {
                out.push({id, name, subjectId});
            }
        }
    }
    return out;
}

export function getPmSubjectNames(): string[] {
    return [...PM_SUBJECTS_ORDER];
}
